import { Head, Link, router } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { PageHeader } from "@/components/layouts/PageHeader"
import { Plus } from "lucide-react"
import { cn } from "@/lib/utils"
import { timeAgo } from "@/lib/date"
import type { Source, SourceType } from "@/types"

const SOURCE_TYPE_LABELS: Record<SourceType, string> = {
  webhook: "Webhook",
  rss: "RSS",
  podcast_rss: "Podcast RSS",
  mimeo: "Mimeo",
  youtube: "YouTube",
  discord: "Discord",
}

const SOURCE_TYPE_COLORS: Record<SourceType, string> = {
  webhook: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  rss: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
  podcast_rss: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
  mimeo: "bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-400",
  youtube: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  discord: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400",
}

interface SourcesIndexProps {
  sources: Source[]
  source_types: string[]
  current_filter?: string
}

export default function Index({ sources, source_types, current_filter }: SourcesIndexProps) {
  return (
    <>
      <Head title="Sources" />
      <div className="space-y-6">
        <PageHeader title="Sources" description="Manage your feed sources.">
          <Button asChild>
            <Link href="/admin/sources/new">
              <Plus className="size-4" />
              New Source
            </Link>
          </Button>
        </PageHeader>

        {source_types.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant={!current_filter ? "default" : "outline"}
              size="sm"
              onClick={() => router.visit("/admin/sources")}
            >
              All
            </Button>
            {source_types.map((type) => (
              <Button
                key={type}
                variant={current_filter === type ? "default" : "outline"}
                size="sm"
                onClick={() => router.visit(`/admin/sources?source_type=${type}`)}
              >
                {SOURCE_TYPE_LABELS[type as SourceType] || type}
              </Button>
            ))}
          </div>
        )}

        {sources.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-muted-foreground text-sm">
              {!current_filter
                ? "No sources yet. Create your first source to get started."
                : "No sources match the selected filter."}
            </p>
            {!current_filter && (
              <Button asChild className="mt-4">
                <Link href="/admin/sources/new">
                  <Plus className="size-4" />
                  Create Source
                </Link>
              </Button>
            )}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Capture</TableHead>
                <TableHead className="text-right">Captures</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sources.map((source) => (
                <TableRow
                  key={source.id}
                  className="cursor-pointer"
                  onClick={() => router.visit(`/admin/sources/${source.id}`)}
                >
                  <TableCell className="font-medium">
                    <Link
                      href={`/admin/sources/${source.id}`}
                      className="hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {source.name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className={cn("border-0", SOURCE_TYPE_COLORS[source.source_type])}>
                      {SOURCE_TYPE_LABELS[source.source_type]}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={cn(
                        source.category === "owned"
                          ? "border-blue-300 text-blue-700 dark:border-blue-700 dark:text-blue-400"
                          : "border-purple-300 text-purple-700 dark:border-purple-700 dark:text-purple-400"
                      )}
                    >
                      {source.category === "owned" ? "Owned" : "Watched"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={source.enabled ? "default" : "secondary"}>
                      {source.enabled ? "Enabled" : "Disabled"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {timeAgo(source.last_successful_capture_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    {source.captures_count ?? 0}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </>
  )
}
